import { useEffect, useMemo, useRef, useState } from 'react';
import { Attachment, AttachmentScanResult } from '@amzn/awscat-pattern-library-service-model';
import { generatePath } from 'react-router-dom';

import RouterPaths from '../../common/RouterPaths';
import { useAttachmentManagementService } from '../../contexts/AttachmentManagementServiceContext';
import { log } from '../../loggers/Logger';
import { useContent } from '../v2-hooks/useContent';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

/** One read-only attachment row. */
export interface AttachmentReadonlyRow {
  readonly attachmentId: string;
  /** Decoded display filename. */
  readonly filename: string;
  /** Download path; undefined when the file didn't pass the virus scan and must not be offered. */
  readonly downloadPath: string | undefined;
}

/** Read-only presentation state for the Attachments tab on the view page. */
export interface AttachmentsReadonlyTabViewModel {
  readonly rows: readonly AttachmentReadonlyRow[];
  /** True while draft attachment metadata is being fetched from the server. */
  readonly isLoadingAttachments: boolean;
  /** True when one or more attachments failed to load — the list is incomplete, not empty. */
  readonly isSomeAttachmentLoadFailed: boolean;
}

function decodeFilename(filename: string): string {
  try {
    return decodeURIComponent(filename);
  } catch {
    return filename;
  }
}

function toRow(contentId: string, attachment: Attachment): AttachmentReadonlyRow {
  // Only clean files are downloadable; infected or unscanned files show as
  // plain text so the link never points at unsafe content.
  const isClean = attachment.scanResult === AttachmentScanResult.OK;
  return {
    attachmentId: attachment.attachmentId,
    filename: decodeFilename(attachment.filename),
    downloadPath: isClean
      ? generatePath(RouterPaths.GET_ATTACHMENTS, {
          contentId,
          attachmentId: attachment.attachmentId,
        })
      : undefined,
  };
}

/**
 * Projects the document's attachments into read-only download rows.
 * For drafts, resolves attachment IDs via the attachment service.
 * For publications and finalizing, uses the pre-resolved attachment list.
 * Returns undefined while no document is loaded — the view renders nothing
 * during that transient state rather than crashing.
 */
export function useAttachmentsReadonlyTabViewModel(): AttachmentsReadonlyTabViewModel | undefined {
  const document = useLoadedDocument();
  const content = useContent();
  const attachmentService = useAttachmentManagementService();

  const contentId = document?.contentId ?? '';
  const draftIdsKey = document?.kind === 'draft' ? (content?.attachments ?? []).join('|') : '';

  const [draftAttachments, setDraftAttachments] = useState<readonly Attachment[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(draftIdsKey !== '');
  const [isSomeAttachmentLoadFailed, setIsSomeAttachmentLoadFailed] = useState(false);
  const fetchedIdsRef = useRef<Set<string>>(new Set());

  // When draft attachment IDs change, fetch only the ones not already resolved.
  useEffect(() => {
    // Reset when there's nothing to fetch.
    if (!contentId || draftIdsKey === '') {
      setDraftAttachments([]);
      setIsLoadingAttachments(false);
      setIsSomeAttachmentLoadFailed(false);
      fetchedIdsRef.current = new Set();
      return;
    }

    // Determine which IDs are new since the last fetch.
    const incomingIds = draftIdsKey.split('|');
    const newIds = incomingIds.filter((id) => !fetchedIdsRef.current.has(id));

    // Drop attachments whose IDs were removed (e.g. user deleted one).
    const incomingSet = new Set(incomingIds);
    setDraftAttachments((prev) =>
      prev.filter((attachment) => incomingSet.has(attachment.attachmentId)),
    );
    fetchedIdsRef.current = new Set([...fetchedIdsRef.current].filter((id) => incomingSet.has(id)));

    // Clear the stale failure flag on every ID change; any still-failing fetch
    // re-sets it below. Without this, removing a failed attachment leaves the
    // flag stuck true because the early return below skips the reset.
    setIsSomeAttachmentLoadFailed(false);

    // Nothing new to fetch — already up to date.
    if (newIds.length === 0) return;

    // Fetch only the new IDs and append to existing results.
    let cancelled = false;
    setIsLoadingAttachments(true);
    void Promise.allSettled(newIds.map((id) => attachmentService.getAttachment(contentId, id)))
      .then((results) => {
        if (cancelled) return;

        // Keep successfully-resolved attachments so one failed fetch can't drop the rest.
        const resolved = results
          .filter((r): r is PromiseFulfilledResult<Attachment> => r.status === 'fulfilled')
          .map((r) => r.value);
        setDraftAttachments((prev) => [...prev, ...resolved]);
        for (const attachment of resolved) fetchedIdsRef.current.add(attachment.attachmentId);

        // Surface failed fetches; leaving their IDs unmarked lets a later change retry them.
        let hasFailure = false;
        for (const result of results) {
          if (result.status === 'rejected') {
            hasFailure = true;
            log.error({ err: result.reason, contentId }, 'Failed to load draft attachment');
          }
        }
        if (hasFailure) setIsSomeAttachmentLoadFailed(true);
      })
      .finally(() => {
        // Always clear the spinner, even when every fetch failed.
        if (!cancelled) setIsLoadingAttachments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attachmentService, contentId, draftIdsKey]);

  const rows = useMemo(() => {
    if (!document) return [];
    if (document.kind === 'draft') {
      // Order rows by content.attachments so display order stays stable and
      // matches the publication/finalizing paths, regardless of fetch order.
      const byId = new Map(draftAttachments.map((a) => [a.attachmentId, a]));
      return (content?.attachments ?? [])
        .map((id) => byId.get(id))
        .filter((a): a is Attachment => a !== undefined)
        .map((a) => toRow(document.contentId, a));
    }
    return document.attachments.map((a) => toRow(document.contentId, a));
  }, [content, document, draftAttachments]);

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  return { rows, isLoadingAttachments, isSomeAttachmentLoadFailed };
}
